/**
 * Transport du formulaire Contact de UniTech Engineering.
 *
 * Contrat inchangé : POST {REACT_APP_UE_API_URL}/api/contact
 * avec Content-Type: application/json, X-API-Key et le payload original.
 * Aucun changement de champ, aucune nouvelle requête, aucun retry.
 *
 * REMARQUE SECURITE : une variable REACT_APP_* est publiée dans le bundle
 * JavaScript. La clé X-API-Key actuelle n'est donc PAS un secret côté web.
 */

const API_PATH = "/api/contact";

class ContactApiError extends Error {
  constructor(code, message, httpStatus) {
    super(message);
    this.name = "ContactApiError";
    this.code = code;
    if (httpStatus !== undefined) this.httpStatus = httpStatus;
  }
}

function getApiConfiguration() {
  // Variables de build de Create React App : pas de clé à copier dans le code.
  const baseUrl = (process.env.REACT_APP_UE_API_URL || "").trim();
  const apiKey = process.env.REACT_APP_UE_API_KEY || "";

  if (!baseUrl) {
    throw new ContactApiError(
      "API_URL_MISSING",
      "REACT_APP_UE_API_URL est absente du build React."
    );
  }
  if (!apiKey) {
    throw new ContactApiError(
      "API_KEY_MISSING",
      "REACT_APP_UE_API_KEY est absente du build React."
    );
  }

  let parsed;
  try {
    parsed = new URL(baseUrl);
  } catch {
    throw new ContactApiError("API_URL_INVALID", "REACT_APP_UE_API_URL est invalide.");
  }
  if (
    !["https:", "http:"].includes(parsed.protocol) ||
    !parsed.hostname || parsed.username || parsed.password ||
    parsed.search || parsed.hash
  ) {
    throw new ContactApiError("API_URL_INVALID", "REACT_APP_UE_API_URL est invalide.");
  }

  // Compatible avec une URL de base, avec une barre finale, ou avec
  // /api/contact déjà inclus dans la configuration.
  const cleanPath = parsed.pathname.replace(/\/+$/, "");
  parsed.pathname = cleanPath.endsWith(API_PATH)
    ? cleanPath
    : `${cleanPath}${API_PATH}`;
  return { endpoint: parsed.toString(), apiKey };
}

function errorForHttpStatus(status) {
  if (status === 401 || status === 403) {
    return new ContactApiError("API_AUTH_REJECTED", "L'API a refusé l'autorisation de la requête.", status);
  }
  if (status === 404 || status === 405) {
    return new ContactApiError("API_ROUTE_NOT_FOUND", "La route POST /api/contact est indisponible.", status);
  }
  if (status === 429) {
    return new ContactApiError("API_RATE_LIMITED", "Le serveur limite temporairement les demandes.", status);
  }
  if (status >= 500) {
    return new ContactApiError("API_SERVER_ERROR", "Erreur du serveur responsable de l'envoi.", status);
  }
  return new ContactApiError("API_HTTP_ERROR", "L'API a refusé la demande.", status);
}

function parseSuccessfulResponse(body, contentType) {
  const trimmedBody = body.trim();
  if (!trimmedBody) return undefined; // Comportement historique, dont HTTP 204.

  // Rejet du fallback HTML (ex. index.html SPA, même renvoyé avec HTTP 200).
  if (/\btext\/html\b/i.test(contentType) ||
      /^\s*<(?:!doctype|html|head|body)\b/i.test(trimmedBody)) {
    throw new ContactApiError(
      "API_RETURNED_HTML",
      "L'URL /api/contact renvoie une page HTML au lieu d'une réponse d'API."
    );
  }

  if (/\bapplication\/json\b|\+json\b/i.test(contentType) ||
      trimmedBody.startsWith("{") || trimmedBody.startsWith("[")) {
    let data;
    try {
      data = JSON.parse(trimmedBody);
    } catch {
      throw new ContactApiError("API_INVALID_JSON", "La réponse JSON de l'API est invalide.");
    }
    if (data === null || typeof data !== "object" || Array.isArray(data)) {
      throw new ContactApiError("API_INVALID_JSON", "Le format de réponse de l'API est invalide.");
    }
    if (data.success === false || data.ok === false || data.sent === false ||
        Boolean(data.error) ||
        ["error", "failed", "failure", "rejected"].includes(String(data.status || "").toLowerCase())) {
      throw new ContactApiError("API_BUSINESS_REJECTED", "L'API a signalé un échec d'envoi.");
    }
    return data.message; // Contrat de retour historique conservé.
  }

  // Compatibilité avec l'API historique si elle retourne du texte brut.
  return trimmedBody;
}

export async function sendContactEmail(payload) {
  try {
    const { endpoint, apiKey } = getApiConfiguration();
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-API-Key": apiKey,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw errorForHttpStatus(response.status);

    const contentType = response.headers.get("content-type") || "";
    const body = await response.text();
    return parseSuccessfulResponse(body, contentType);
  } catch (error) {
    const diagnostic = error instanceof ContactApiError
      ? error
      : new ContactApiError(
          "NETWORK_OR_CORS_ERROR",
          "Impossible de joindre l'API Contact (réseau, CORS ou TLS)."
        );

    // Diagnostic strictement technique, sans clé API ni données personnelles.
    // Le formulaire existant conserve son message d'erreur utilisateur.
    console.error("[UNITECH Contact API] Envoi échoué", {
      code: diagnostic.code,
      httpStatus: diagnostic.httpStatus ?? null,
    });
    throw diagnostic;
  }
}
